import { z } from 'zod'
import { DISCIPLINES, type Discipline } from './gradeConfig'

/**
 * Competitions — a gym-scoped climbing competition over a chosen set of routes.
 *
 * Two formats:
 *  - `long_running` spans a week/month/year; members log routes any time inside
 *    the window.
 *  - `single_day` runs on one day, optionally split into time-slot groups
 *    (`competition_sessions`). Zero sessions means everyone, all day.
 *
 * Semis and finals are deliberately out of scope — those are referee-scored and
 * are not modelled here.
 */

/**
 * What a competition covers. `combined` is one event with one registration
 * and one set of categories. Bouldering and lead are ranked on separate
 * boards, and a third General board adds the two together. Which board a top
 * lands on is decided by the route's section, the same rule as
 * `disciplineForSection`.
 *
 * Kept apart from `DISCIPLINES` on purpose: that list keys the grade config,
 * and there is no such thing as a combined grade.
 */
export const COMPETITION_DISCIPLINES = [...DISCIPLINES, 'combined'] as const
export type CompetitionDiscipline = (typeof COMPETITION_DISCIPLINES)[number]

/** The scoreboards a competition has, in display order: one, or two when combined. */
export function competitionBoards(discipline: CompetitionDiscipline): Discipline[] {
  return discipline === 'combined' ? ['boulder', 'lead'] : [discipline]
}

/** A scoreboard a reader can pick: a discipline, or the sum of both. */
export type CompetitionBoard = Discipline | 'general'

/**
 * The scoreboards a competition shows, in tab order. A combined competition
 * leads with General, bouldering plus lead. `competitionBoards` stays the list
 * of boards a route can be scored on, which General is not.
 */
export function competitionBoardTabs(discipline: CompetitionDiscipline): CompetitionBoard[] {
  return discipline === 'combined' ? ['general', 'boulder', 'lead'] : [discipline]
}

export const COMPETITION_FORMATS = ['long_running', 'single_day'] as const
export type CompetitionFormat = (typeof COMPETITION_FORMATS)[number]

/**
 * Pluggable scoring. Adding a formula is a new value here, a new CHECK value on
 * `competitions.scoring_type`, and one more branch in `get_competition_standings` —
 * the response shape never changes, so no caller is rewritten.
 *
 * Neither formula reads a grade. Routes set for a competition have none (they
 * are numbered, not graded), so grade-based scoring (`gym_standings`, the gym
 * leaderboard's own formula) was removed as a competition option.
 *
 * `thousand_split` lets the field decide: every route is worth 1000
 * points, split between the climbers **in the same category** who topped it, and
 * a flash adds 10% of that share on top. Two entrants top a route and one
 * flashed: 500 and 550. Grade does not enter into it, a route can therefore pay
 * out more than 1000 in total, and scores fall as more people top a route.
 *
 * `tops_count` is the plainest: one point per route topped. Grade, flashes and
 * attempts do not enter into it, so 20 tops beats 19, and climbers on the same
 * count share the place.
 */
/** The longest number or label a competition route can carry, e.g. "12" or "A3". */
export const MAX_COMPETITION_ROUTE_LABEL = 20

export const COMPETITION_SCORING_TYPES = ['thousand_split', 'tops_count'] as const
export type CompetitionScoringType = (typeof COMPETITION_SCORING_TYPES)[number]

/**
 * Competitors are ranked in gender categories. These deliberately reuse the
 * vocabulary of `profiles.gender` and the existing `/gym-standings?gender=`
 * param so no mapping layer is needed anywhere.
 */
export const COMPETITION_CATEGORIES = ['male', 'female'] as const
export type CompetitionCategory = (typeof COMPETITION_CATEGORIES)[number]

/**
 * A profile's competition category, or null when the member has not given a
 * gender we can rank on (`prefer_not_to_say`, or never set). Null is what gates
 * joining — the member is asked to complete their profile first.
 */
export function categoryForGender(gender: string | null | undefined): CompetitionCategory | null {
  return gender === 'male' || gender === 'female' ? gender : null
}

/** Wall-clock local datetime, "YYYY-MM-DDTHH:MM" with optional seconds. */
const WALL_CLOCK = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/

const wallClock = z.string().regex(WALL_CLOCK, 'Must be a date and time')

export const competitionSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(200),
    description: z.string().max(4000).nullable().optional(),
    prizes: z.string().max(4000).nullable().optional(),
    discipline: z.enum(COMPETITION_DISCIPLINES),
    format: z.enum(COMPETITION_FORMATS),
    scoring_type: z.enum(COMPETITION_SCORING_TYPES).default('thousand_split'),
    starts_at: wallClock,
    ends_at: wallClock,
    published: z.boolean().default(false),
  })
  .refine((c) => c.ends_at > c.starts_at, {
    message: 'End must be after start',
    path: ['ends_at'],
  })

export type CompetitionInput = z.infer<typeof competitionSchema>

export const competitionSessionSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(200),
    starts_at: wallClock,
    ends_at: wallClock,
    // null = uncapped
    capacity: z.number().int().min(1).nullable().optional(),
    sort_order: z.number().int().min(0).default(0),
  })
  .refine((s) => s.ends_at > s.starts_at, {
    message: 'End must be after start',
    path: ['ends_at'],
  })

export type CompetitionSessionInput = z.infer<typeof competitionSessionSchema>
